import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';

export interface ActionMenuItem {
    id: string;
    label: React.ReactNode;
    icon?: React.ReactNode;
    onClick?: (e: React.MouseEvent) => void;
    visible?: boolean;
    disabled?: boolean;
    danger?: boolean;
    divider?: boolean;
    closeOnClick?: boolean;
}

export interface ActionsMenuProps {
    items: ActionMenuItem[];
    isOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    toggleIcon?: React.ReactNode;
    toggleClassName?: string;
    tooltipId?: string;
    tooltipContent?: string;
    renderToggle?: (props: {
        ref: React.RefObject<HTMLButtonElement>;
        onClick: (e: React.MouseEvent) => void;
        isOpen: boolean;
    }) => React.ReactNode;

    menuWidth?: number;
    align?: 'start' | 'end';
    zIndex?: number;
    className?: string;
    closeOnScroll?: boolean;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
    items,
    isOpen: controlledOpen,
    onOpenChange,
    toggleIcon,
    toggleClassName = 'btn btn-icon btn-active-light-dark btn-sm ms-0',
    tooltipId,
    tooltipContent,
    renderToggle,
    menuWidth = 170,
    align = 'end',
    zIndex = 2000,
    className = '',
    closeOnScroll = true,
}) => {
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = isControlled ? (controlledOpen as boolean) : internalOpen;

    const setOpen = useCallback(
        (next: boolean) => {
            if (!isControlled) setInternalOpen(next);
            onOpenChange?.(next);
        },
        [isControlled, onOpenChange]
    );

    const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const toggleRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const computePosition = useCallback(() => {
        const btn = toggleRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = align === 'end' ? rect.right - menuWidth : rect.left;
        let top = rect.bottom + 4;

        if (left < 8) left = 8;
        if (left + menuWidth > viewportWidth - 8) left = viewportWidth - menuWidth - 8;

        const estimatedMenuHeight = Math.min(items.length * 36 + 16, 320);
        if (top + estimatedMenuHeight > viewportHeight && rect.top - estimatedMenuHeight > 0) {
            top = rect.top - estimatedMenuHeight - 4;
        }

        setCoords({ top, left });
    }, [align, menuWidth, items.length]);

    const handleToggleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) computePosition();
        setOpen(!isOpen);
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleOutsideClick = (e: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node) &&
                toggleRef.current &&
                !toggleRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };

        const handleReposition = () => {
            if (closeOnScroll) {
                setOpen(false);
            } else {
                computePosition();
            }
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', handleOutsideClick);
        window.addEventListener('scroll', handleReposition, true);
        window.addEventListener('resize', handleReposition);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            window.removeEventListener('scroll', handleReposition, true);
            window.removeEventListener('resize', handleReposition);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, closeOnScroll, computePosition, setOpen]);

    const visibleItems = useMemo(() => items.filter((i) => i.visible !== false), [items]);

    return (
        <>
            {renderToggle ? (
                renderToggle({ ref: toggleRef, onClick: handleToggleClick, isOpen })
            ) : (
                <button
                    ref={toggleRef}
                    type="button"
                    className={toggleClassName}
                    data-tooltip-id={tooltipId}
                    data-tooltip-content={tooltipContent}
                    data-tooltip-place="top"
                    onClick={handleToggleClick}
                >
                    {toggleIcon ?? <i className="bi bi-three-dots fs-7"></i>}
                </button>
            )}

            {isOpen &&
                visibleItems.length > 0 &&
                ReactDOM.createPortal(
                    <div
                        ref={menuRef}
                        className={`menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 px-1 py-1 fs-2 show ${className}`}
                        style={{
                            position: 'fixed',
                            top: coords.top,
                            left: coords.left,
                            width: menuWidth,
                            zIndex,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                            background: '#fff',
                            border: '1px solid #E9ECEF',
                            borderRadius: 6,
                        }}
                    >
                        {visibleItems.map((item) => (
                            <React.Fragment key={item.id}>
                                {item.divider && <div className="separator mb-1 mt-1 opacity-75"></div>}
                                <div className={item.danger ? 'menu-hover-bg-danger' : 'menu-hover-bg-sec'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (item.disabled) return;
                                        item.onClick?.(e);
                                        if (item.closeOnClick !== false) setOpen(false);
                                    }}
                                >
                                    {item.icon}
                                </div>
                            </React.Fragment>
                        ))}
                    </div>,
                    document.body
                )}
        </>
    );
};

export default ActionsMenu;